import { Button } from "@/components/ui/button";
import { AREA_AUDIT_KIND_LABEL } from "@/lib/tina/area-audit";
import type { AreaAuditKindCounts } from "@/pages/tina/audits/area/hooks/use-area-audit-repository";
import { AREA_AUDIT_KINDS, type AreaAuditKind } from "@/types/tina";

interface AreaKindCountStripProps {
  counts: AreaAuditKindCounts | undefined;
  isLoading: boolean;
  selected: AreaAuditKind | null;
  onSelect: (kind: AreaAuditKind | null) => void;
}

/**
 * The backlog's shape as eight clickable counts — the Area tab's kind filter, built like
 * the Venue tab's `KindCountStrip`. Each count is the server's own total over the current
 * status/severity/city view, never a tally of the loaded page.
 *
 * A count of zero still renders, greyed: "this kind is clear" is information. A count the
 * strip could not get renders as "—", never as 0.
 */
export function AreaKindCountStrip({
  counts,
  isLoading,
  selected,
  onSelect,
}: AreaKindCountStripProps) {
  const pending = isLoading ? "…" : "—";
  const total = counts
    ? AREA_AUDIT_KINDS.reduce((sum, kind) => sum + (counts[kind] ?? 0), 0)
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant={selected === null ? "default" : "outline"}
        size="sm"
        className="h-7 text-xs"
        onClick={() => onSelect(null)}
      >
        Everything
        <span className="ml-2 font-mono tabular-nums">
          {total === null ? pending : total}
        </span>
      </Button>
      {AREA_AUDIT_KINDS.map((kind) => {
        const count = counts?.[kind];
        return (
          <Button
            key={kind}
            variant={selected === kind ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() => onSelect(selected === kind ? null : kind)}
          >
            <span className={count === 0 ? "text-muted-foreground" : undefined}>
              {AREA_AUDIT_KIND_LABEL[kind]}
            </span>
            <span className="ml-2 font-mono tabular-nums">
              {count === undefined ? pending : count}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
